import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PhoneInput, normalizePhone } from '../components/ui/phone-input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '../components/ui/select';
import { ArrowLeft, Plus, Send, Phone, Calendar, User, Package, RefreshCw, ChevronDown, ChevronUp, RotateCcw, Link, FileText, ShoppingCart, Image as ImageIcon, BookOpen, Trash2, X, MapPin, ExternalLink, Eye, Pencil, Copy, Smile, Reply, CornerUpLeft, CheckCheck, Camera, Share2, ClipboardList } from 'lucide-react';
import { refreshBadge } from '../services/badgeService';
import { useToast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { fileService } from '../services/apiService';
import EstimateFormDialog from '../components/EstimateFormDialog';
import RepairEstimateDialog from '../components/RepairEstimateDialog';
import GoldRateGateDialog from '../components/GoldRateGateDialog';
import ClientMemoryCard from '../components/ClientMemoryCard';
import EstimateDetailPopup from '../components/EstimateDetailPopup';
import MediaTab from '../components/session/MediaTab';
import SessionHeader from '../components/session/SessionHeader';
import LeadsTab from '../components/session/LeadsTab';
import ChatTab from '../components/session/ChatTab';
import { SessionContext } from '../contexts/SessionContext';
import { useEstimateFlow } from '../hooks/useEstimateFlow';

const SessionDetail = () => {
  const { sessionId } = useParams();
  const { currentUser, isAdvisor } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [session, setSession] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'leads' | 'media'
  const [newMessage, setNewMessage] = useState('');
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [taggablePeople, setTaggablePeople] = useState([]);
  const [showLeads, setShowLeads] = useState(true);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [repairPhotoPromptOpen, setRepairPhotoPromptOpen] = useState(false);
  const [pendingPhotoCapture, setPendingPhotoCapture] = useState(null); // 'camera' | 'gallery' — fired once ChatTab mounts
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [notebookOpen, setNotebookOpen] = useState(false);
  const [notebookLead, setNotebookLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteImages, setNoteImages] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({}); // { lead_id: { text, images } }
  const [uploadingNote, setUploadingNote] = useState(false);
  const noteImageRef = useRef(null);
  const noteTextareaRef = useRef(null);
  const [salesmen, setSalesmen] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [parkedReasons, setParkedReasons] = useState([]);
  const [wonReasons, setWonReasons] = useState([]);
  const [leadForm, setLeadForm] = useState({ product_name: '', custom_product_name: '', main_group: '', description: '', estimated_value: '', occasion_date: '', follow_up_date: '', repair_weight: '' });
  // Pre-fill follow-up date to today+3 whenever Add Lead dialog opens
  useEffect(() => {
    if (addLeadOpen && !leadForm.follow_up_date) {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      setLeadForm(f => ({ ...f, follow_up_date: d.toISOString().split('T')[0] }));
    }
  }, [addLeadOpen, leadForm.follow_up_date]);
  const [editLeadTarget, setEditLeadTarget] = useState(null); // lead object being edited
  const [editLeadForm, setEditLeadForm] = useState({ product_name: '', custom_product_name: '', main_group: '', description: '', estimated_value: '', occasion_date: '' });
  const [statusForm, setStatusForm] = useState({ status: '', reason: '', reason_comment: '', follow_up_date: '', follow_up_salesman_id: '', park_action: '' });
  const [parkActions, setParkActions] = useState([]);
  const [catalogGroups, setCatalogGroups] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [linkedEstimateDetails, setLinkedEstimateDetails] = useState({}); // lead_id -> [estimate objects]
  const [leadRepairEstimates, setLeadRepairEstimates] = useState({}); // lead_id -> [repair estimate objects]

  // ── Chat enhancements ──────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const highlightMessageId = searchParams.get('highlight');
  const [readState, setReadState] = useState(null);
  const [mentionJumpIndex, setMentionJumpIndex] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);   // { _id, sender_name, content, type }
  const [activeContextMsg, setActiveContextMsg] = useState(null); // message showing action bar
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [reactionTooltip, setReactionTooltip] = useState(null); // { msgId, emoji, users }
  const pressTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Old Gold exchange
  const [isOldGoldOpen, setIsOldGoldOpen] = useState(false);
  const [oldGoldForm, setOldGoldForm] = useState({
    customerPhone: '', customerName: '',
    grossWeight: '', lessDust: '', lessStones: '',
    approxPurity: '', refiningPercentage: '',
    goldRate: '', cashRequired: false, cashDeductionPercentage: '',
  });
  const [oldGoldStones, setOldGoldStones] = useState([]);
  const [currentOldGoldStone, setCurrentOldGoldStone] = useState({ name: '', weight: '', rate: '', amount: '' });
  const [oldGoldLeadId, setOldGoldLeadId] = useState(null);
  const [sessionExchangeEstimates, setSessionExchangeEstimates] = useState([]);
  const [viewExchangeEst, setViewExchangeEst] = useState(null);
  const [todayGoldPrices, setTodayGoldPrices] = useState(null);

  const [deleteLeadTarget, setDeleteLeadTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const handleDeleteLead = async () => {
    if (!deleteLeadTarget) return;
    try {
      await api.delete(`/crm/leads/${deleteLeadTarget._id}`);
      toast({ title: `Lead "${deleteLeadTarget.product_name}" deleted` });
      setDeleteLeadTarget(null);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  // Visit log
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({ date: '', summary: '' });
  const [submittingVisit, setSubmittingVisit] = useState(false);

  // Inline client info editing
  const [editingClientInfo, setEditingClientInfo] = useState(false);
  const [clientInfoForm, setClientInfoForm] = useState({ client_name: '', client_phone: '' });
  const [savingClientInfo, setSavingClientInfo] = useState(false);

  const handleSaveClientInfo = async () => {
    if (!clientInfoForm.client_name || !clientInfoForm.client_phone) {
      toast({ title: 'Both name and phone are required', variant: 'destructive' });
      return;
    }
    setSavingClientInfo(true);
    try {
      await api.patch(`/crm/sessions/${sessionId}/client-info`, clientInfoForm);
      toast({ title: 'Client details saved' });
      setEditingClientInfo(false);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setSavingClientInfo(false);
    }
  };

  const canManageLeads = currentUser?.role === 'admin' || currentUser?.role === 'owner' || currentUser?.role === 'salesman';
  const canCreateSession = currentUser?.role === 'admin' || currentUser?.role === 'owner' || currentUser?.role === 'crm';
  const canReopenSession = currentUser?.role === 'admin' || currentUser?.role === 'stock_supervisor';
  const canLogVisit = ['admin', 'owner', 'crm', 'salesman', 'stock_supervisor'].includes(currentUser?.role);

  const fetchAll = useCallback(async () => {
    try {
      const [sessRes, leadsRes, msgsRes] = await Promise.all([
        api.get(`/crm/sessions/${sessionId}`),
        api.get(`/crm/sessions/${sessionId}/leads`),
        api.get(`/crm/sessions/${sessionId}/messages`),
      ]);
      setSession(sessRes.data);
      setLeads(leadsRes.data);
      setMessages(msgsRes.data);
      setLoading(false); // unblock chat UI so chatContainerRef is ready for scroll-init effect

      // Fetch linked estimate details for leads that have them
      const estimateMap = {};
      for (const ld of leadsRes.data) {
        const estIds = ld.linked_estimate_ids || (ld.linked_estimate_id ? [ld.linked_estimate_id] : []);
        if (estIds.length > 0) {
          const estimates = [];
          for (const eid of estIds) {
            try {
              const estRes = await api.get(`/estimates/by-id/${eid}`);
              estimates.push(estRes.data);
            } catch (e) { /* estimate may have been deleted */ }
          }
          if (estimates.length > 0) estimateMap[ld._id] = estimates;
        }
      }
      setLinkedEstimateDetails(estimateMap);

      // Fetch repair-estimate ledgers for this session, grouped by lead.
      try {
        const repRes = await api.get(`/repair-estimates?session_id=${sessionId}`);
        const repMap = {};
        for (const re of (repRes.data || [])) {
          if (!re.lead_id) continue;
          (repMap[re.lead_id] = repMap[re.lead_id] || []).push(re);
        }
        setLeadRepairEstimates(repMap);
      } catch (e) { /* ignore */ }

      // Fetch exchange (old gold) estimates for this session
      try {
        const exRes = await api.get(`/exchange-estimates?session_id=${sessionId}`);
        setSessionExchangeEstimates(exRes.data);
      } catch (e) { /* ignore */ }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Estimate flow hook (all estimate dialog state + handlers) ──────────
  const {
    linkEstimateOpen, setLinkEstimateOpen,
    linkLeadTarget, setLinkLeadTarget,
    customerEstimates,
    createEstimateOpen, setCreateEstimateOpen,
    createEstimateLead,
    duplicateEstimateData,
    viewEstimateOpen, setViewEstimateOpen,
    viewEstimateData, setViewEstimateData,
    viewEstimateLeadContext,
    showLinkPrompt, setShowLinkPrompt,
    pendingLinkEstimate, setPendingLinkEstimate,
    showPrintPrompt, setShowPrintPrompt,
    printPromptEstimate,
    repairEstOpen, setRepairEstOpen,
    repairEstLead,
    estimatePrefill,
    openLinkEstimate,
    handleLinkEstimate,
    handleCreateEstimateForLead,
    handleConfirmRepairEstimate,
    handleEstimateSaved,
    handleDuplicateEstimate,
    handleEstimateDeleted,
    handleConfirmEstimate,
    handleEstimateConfirmed,
    openEstimateDetail,
    handleConvertToOrder,
    goldGateDialogProps,
  } = useEstimateFlow({ session, sessionId, fetchAll, navigate, toast, linkedEstimateDetails, leadRepairEstimates });

  // Track this session as last-viewed so LeadSessions can optimistically clear its unread dot
  useEffect(() => {
    sessionStorage.setItem('last_viewed_session_id', sessionId);
  }, [sessionId]);

  // Mark all messages as read when session opens, and when user re-focuses the tab.
  // Also re-fetch messages on visibility-change so messages sent while the app was
  // in the background (WebSocket disconnected) are always visible when the user returns.
  useEffect(() => {
    const markRead = () => {
      api.post(`/crm/sessions/${sessionId}/messages/read`)
        .then(() => refreshBadge())  // Update badge after marking session as read
        .catch(() => {});
    };
    markRead();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        markRead();
        // Re-fetch messages in case new ones arrived while the WebSocket was offline
        api.get(`/crm/sessions/${sessionId}/messages`)
          .then(r => setMessages(r.data))
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [sessionId]);

  // Save last-read position on unmount (for scroll position memory)
  useEffect(() => {
    return () => {
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?._id) {
          api.post(`/crm/sessions/${sessionId}/mark-last-read`, { last_message_id: lastMsg._id }).catch(() => {});
        }
        return prev;
      });
    };
  }, [sessionId]);

  // Scroll position memory + deep link highlight — fires after first messages load
  const scrollInitDone = useRef(false);
  // Reset scroll-init gate whenever the session changes so deep-link highlights work
  // correctly when the user navigates between sessions without a full remount.
  useEffect(() => {
    scrollInitDone.current = false;
  }, [sessionId]);
  useEffect(() => {
    if (messages.length === 0 || scrollInitDone.current) return;
    scrollInitDone.current = true;
    const container = chatContainerRef.current;
    if (!container) return;

    // Deep link: scroll to highlighted message
    if (highlightMessageId) {
      setTimeout(() => {
        const el = document.getElementById(`msg-${highlightMessageId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('msg-highlight-flash');
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }, 400);
      return;
    }

    // Scroll position memory: land at first unread message
    if (readState?.last_read_message_id) {
      const idx = messages.findIndex(m => m._id === readState.last_read_message_id);
      if (idx >= 0 && idx < messages.length - 1) {
        // There are unread messages after the last read one — scroll to first unread
        const firstUnread = messages[idx + 1];
        setTimeout(() => {
          const el = document.getElementById(`msg-${firstUnread._id}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else container.scrollTop = container.scrollHeight;
        }, 400);
        return;
      }
    }
    // Default: scroll to bottom
    container.scrollTop = container.scrollHeight;
  }, [messages.length]);
  useEffect(() => {
    api.get('/gold-prices/today').then(r => setTodayGoldPrices(r.data)).catch(() => {});
  }, []);
  // ── WebSocket: real-time message delivery ───────────────────────────────
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const wsPingRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !sessionId) return;

    function connect() {
      const base = process.env.REACT_APP_BACKEND_URL
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
      const ws = new WebSocket(`${base}/api/ws/sessions/${sessionId}?token=${token}`);

      ws.onmessage = (evt) => {
        if (evt.data === 'pong') return;
        try {
          const msg = JSON.parse(evt.data);

          // Handle read receipts — update read_by on all local messages
          if (msg.type === 'read_receipt') {
            setMessages(prev => prev.map(m =>
              m.read_by && !m.read_by.includes(msg.reader_id)
                ? { ...m, read_by: [...m.read_by, msg.reader_id] }
                : m
            ));
            return;
          }

          // Handle emoji reaction updates
          if (msg.type === 'reaction') {
            setMessages(prev => prev.map(m =>
              m._id === msg.message_id ? { ...m, reactions: msg.reactions } : m
            ));
            return;
          }

          setMessages(prev => {
            // 1. Ignore exact duplicates already in state
            if (prev.some(m => m._id && m._id === msg._id)) return prev;

            // 2. For image messages, check if we already have this URL (prevents duplicates)
            if (msg.type === 'image' && prev.some(m => m.content === msg.content && m.type === 'image')) {
              return prev;
            }

            // 3. Replace optimistic placeholder
            const pendingIdx = prev.findIndex(
              m => m._tempId && m.sender_id === msg.sender_id && m.type === msg.type
            );
            if (pendingIdx >= 0) {
              const updated = [...prev];
              updated[pendingIdx] = { ...msg, pending: false };
              return updated;
            }

            return [...prev, msg];
          });
        } catch (err) { /* ignore malformed ws frames */ }
      };

      ws.onopen = () => {
        // Heartbeat every 30 s — keeps the connection alive through idle proxies
        wsPingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onclose = () => {
        clearInterval(wsPingRef.current);
        // Auto-reconnect after 3 s
        wsReconnectRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
      wsRef.current = ws;
    }

    connect();

    return () => {
      clearTimeout(wsReconnectRef.current);
      clearInterval(wsPingRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect loop on unmount
        wsRef.current.close();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    // Only auto-scroll if user is already near the bottom (within 150px).
    // Use direct scrollTop assignment — avoids scrollIntoView() which can
    // scroll the entire browser viewport on mobile (iOS/Android).
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    Promise.all([
      api.get('/users/by-role/salesman').catch(() => ({ data: [] })),
      api.get('/crm/lead-reasons/lost').catch(() => ({ data: [] })),
      api.get('/crm/lead-reasons/parked').catch(() => ({ data: [] })),
      api.get('/crm/lead-reasons/won').catch(() => ({ data: [] })),
      api.get('/catalog/main-groups').catch(() => ({ data: [] })),
      api.get('/catalog/products').catch(() => ({ data: [] })),
      api.get('/crm/park-actions').catch(() => ({ data: [] })),
    ]).then(([s, lr, pr, wr, mg, mp, pa]) => {
      setSalesmen(s.data);
      setLossReasons(lr.data);    // now grouped: [{category, reasons:[{id,reason}]}]
      setParkedReasons(pr.data);
      setWonReasons(wr.data);
      setCatalogGroups(mg.data?.map(i => i.name) || []);
      setCatalogProducts(mp.data?.map(i => i.name) || []);
      setParkActions(pa.data || []);
    });
    // Fetch taggable people for @ mentions (assigned salesman + authority roles, excluding self)
    api.get(`/crm/sessions/${sessionId}/taggable-users`).then(res => {
      setTaggablePeople(res.data || []);
    }).catch(() => {});
    // Fetch read state for scroll position memory
    api.get(`/crm/sessions/${sessionId}/read-state`).then(res => {
      setReadState(res.data || null);
    }).catch(() => {});
  }, []);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const content = newMessage.trim();
    const tempId = `temp_${Date.now()}`;

    // Optimistic update — message appears instantly before server confirms
    const optimisticMsg = {
      _id: tempId,
      _tempId: tempId,
      session_id: sessionId,
      sender_id: currentUser?.id,
      sender_name: currentUser?.username,
      content,
      type: 'text',
      reply_to: replyingTo ? {
        message_id: replyingTo._id,
        sender_name: replyingTo.sender_name,
        content_preview: replyingTo.type === 'image' ? '📷 Image' : replyingTo.content.substring(0, 80),
      } : null,
      timestamp: new Date().toISOString(),
      pending: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    setReplyingTo(null);
    setShowEmojiPicker(false);
    // Reset textarea height back to single line after sending
    if (inputRef.current) {
      inputRef.current.style.height = '36px';
      inputRef.current.style.overflowY = 'hidden';
    }

    try {
      const payload = { content, type: 'text' };
      if (optimisticMsg.reply_to) payload.reply_to = optimisticMsg.reply_to;
      await api.post(`/crm/sessions/${sessionId}/messages`, payload);
      // WebSocket broadcast delivers the confirmed message to all clients.
      // The ws.onmessage handler replaces the optimistic placeholder automatically.
    } catch (e) {
      // Roll back optimistic message on failure
      setMessages(prev => prev.filter(m => m._id !== tempId));
      toast({ title: 'Error sending message', variant: 'destructive' });
    }
  };

  const handleReact = async (messageId, emoji) => {
    if (!messageId || messageId.startsWith('temp_')) return;
    try {
      const res = await api.post(`/crm/sessions/${sessionId}/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m =>
        m._id === messageId ? { ...m, reactions: res.data.reactions } : m
      ));
    } catch (err) { /* reaction rejection is non-critical */ }
  };

  // Helper — extract user id regardless of {id,name} object or legacy string
  const _getRId = (u) => (typeof u === 'object' ? u.id : u);
  const _getRName = (u) => (typeof u === 'object' ? u.name : null);

  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset BOTH inputs so the same photo can be re-selected / camera re-opened
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';

    setUploadingImage(true);

    // Optimistic placeholder — gives instant feedback that upload started
    const tempId = `img_temp_${Date.now()}`;
    const objectUrl = URL.createObjectURL(file);
    const optimisticMsg = {
      _id: tempId,
      _tempId: tempId,
      session_id: sessionId,
      sender_id: currentUser?.id,
      sender_name: currentUser?.username,
      content: objectUrl,
      type: 'image',
      timestamp: new Date().toISOString(),
      pending: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      const result = await fileService.uploadFile(file);
      await api.post(`/crm/sessions/${sessionId}/messages`, { content: result.url, type: 'image' });
      // Don't update the message here - let WebSocket handle the replacement
      // to avoid race conditions that cause duplicates
      // Clean up the temporary object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      // Roll back the optimistic placeholder and revoke the object URL
      setMessages(prev => prev.filter(m => m._id !== tempId));
      URL.revokeObjectURL(objectUrl);
      toast({ title: 'Failed to upload image', description: err.message || 'Please try again', variant: 'destructive' });
    } finally {
      setUploadingImage(false);
    }
  };

  // After the repair-photo nudge, once the Chat tab is mounted, open the camera/gallery.
  useEffect(() => {
    if (activeTab === 'chat' && pendingPhotoCapture) {
      const t = setTimeout(() => {
        if (pendingPhotoCapture === 'camera') cameraInputRef.current?.click();
        else imageInputRef.current?.click();
        setPendingPhotoCapture(null);
      }, 350);
      return () => clearTimeout(t);
    }
  }, [activeTab, pendingPhotoCapture]);

  // Notebook handlers
  const openNotebook = async (lead) => {
    setNotebookLead(lead);
    setNotebookOpen(true);
    // Restore draft if exists
    const draft = noteDrafts[lead._id];
    setNewNote(draft?.text || '');
    setNoteImages(draft?.images || []);
    try {
      const res = await api.get(`/crm/leads/${lead._id}/notes`);
      setLeadNotes(res.data);
    } catch (e) { console.error(e); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() && noteImages.length === 0) return;
    try {
      await api.post(`/crm/leads/${notebookLead._id}/notes`, {
        content: newNote.trim(),
        images: noteImages,
      });
      setNewNote('');
      setNoteImages([]);
      // Clear draft on successful send
      setNoteDrafts(prev => { const next = { ...prev }; delete next[notebookLead._id]; return next; });
      const res = await api.get(`/crm/leads/${notebookLead._id}/notes`);
      setLeadNotes(res.data);
    } catch (e) { toast({ title: 'Failed to add note', variant: 'destructive' }); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/crm/leads/${notebookLead._id}/notes/${noteId}`);
      setLeadNotes(prev => prev.filter(n => n._id !== noteId));
    } catch (e) { toast({ title: 'Failed to delete', variant: 'destructive' }); }
  };

  const handleNoteImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNote(true);
    try {
      const result = await fileService.uploadFile(file);
      setNoteImages(prev => [...prev, result.url]);
    } catch (err) { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploadingNote(false); if (noteImageRef.current) noteImageRef.current.value = ''; }
  };

  const handleAddLead = async () => {
    const actualProduct = leadForm.product_name === '__custom'
      ? leadForm.custom_product_name.trim()
      : leadForm.product_name;
    if (!actualProduct) {
      toast({ title: 'Product name required', variant: 'destructive' });
      return;
    }
    if (!leadForm.follow_up_date) {
      toast({ title: 'Follow-up date required', description: 'Please pick when you will next contact the customer.', variant: 'destructive' });
      return;
    }
    const wasCustom = leadForm.product_name === '__custom';
    const wasRepair = leadForm.main_group === 'Repair';
    try {
      await api.post(`/crm/sessions/${sessionId}/leads`, {
        ...leadForm,
        product_name: actualProduct,
        estimated_value: parseFloat(leadForm.estimated_value) || 0,
        repair_weight: leadForm.main_group === 'Repair' && leadForm.repair_weight ? parseFloat(leadForm.repair_weight) : null,
      });
      setAddLeadOpen(false);
      setLeadForm({ product_name: '', custom_product_name: '', main_group: '', description: '', estimated_value: '', occasion_date: '', follow_up_date: '', repair_weight: '' });
      // If a custom product was used, refresh catalog so it appears in future dropdowns
      if (wasCustom) {
        toast({ title: `'${actualProduct}' added to product catalog` });
        api.get('/catalog/products').then(res => setCatalogProducts(res.data?.map(i => i.name) || []));
      }
      // Repair lead → nudge the salesman to capture photos of the incoming piece
      // into the session chat so production can see exactly what came in.
      if (wasRepair) setRepairPhotoPromptOpen(true);
      fetchAll();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const openEditLead = (lead) => {
    setEditLeadTarget(lead);
    setEditLeadForm({
      product_name: lead.product_name || '',
      custom_product_name: '',
      main_group: lead.main_group || '',
      description: lead.description || '',
      estimated_value: lead.estimated_value > 0 ? String(lead.estimated_value) : '',
      occasion_date: lead.occasion_date || '',
    });
  };

  const handleSaveEditLead = async () => {
    const actualProduct = editLeadForm.product_name === '__custom'
      ? editLeadForm.custom_product_name.trim()
      : editLeadForm.product_name.trim();
    if (!actualProduct) {
      toast({ title: 'Product name required', variant: 'destructive' });
      return;
    }
    try {
      await api.patch(`/crm/leads/${editLeadTarget._id}`, { ...editLeadForm, product_name: actualProduct });
      toast({ title: 'Lead updated' });
      setEditLeadTarget(null);
      fetchAll();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  // Old Gold helpers
  const openOldGoldDialog = (lead) => {
    const rate = todayGoldPrices?.['22K'] || '';
    setOldGoldForm({
      customerPhone: session?.client_phone || '',
      customerName: session?.client_name || '',
      grossWeight: '', lessDust: '', lessStones: '',
      approxPurity: '', refiningPercentage: '',
      goldRate: rate ? String(rate) : '', cashRequired: false, cashDeductionPercentage: '',
    });
    setOldGoldStones([]);
    setCurrentOldGoldStone({ name: '', weight: '', rate: '', amount: '' });
    setOldGoldLeadId(lead?._id || null);
    setIsOldGoldOpen(true);
  };

  const oldGoldCalc = (() => {
    const gw = parseFloat(oldGoldForm.grossWeight) || 0;
    const ld = parseFloat(oldGoldForm.lessDust) || 0;
    const ls = parseFloat(oldGoldForm.lessStones) || 0;
    const netWeight = gw - ld - ls;
    const purity = parseFloat(oldGoldForm.approxPurity) || 0;
    const fineWeight = netWeight * (purity / 100);
    const refPct = parseFloat(oldGoldForm.refiningPercentage) || 0;
    const finalGoldWeight = fineWeight * (1 - refPct / 100);
    const goldRate = parseFloat(oldGoldForm.goldRate) || 0;
    const goldValue = Math.round(finalGoldWeight * goldRate);
    const stonesTotal = Math.round(oldGoldStones.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0));
    const exchangeValue = goldValue + stonesTotal;
    const cashPct = parseFloat(oldGoldForm.cashDeductionPercentage) || 0;
    const finalCash = oldGoldForm.cashRequired ? Math.round(exchangeValue * (1 - cashPct / 100)) : 0;
    return { netWeight, fineWeight, finalGoldWeight, goldValue, stonesTotal, exchangeValue, finalCash };
  })();

  const handleAddOldGoldStone = () => {
    const w = parseFloat(currentOldGoldStone.weight);
    const r = parseFloat(currentOldGoldStone.rate);
    if (!w || !r) {
      toast({ title: 'Enter weight and rate', variant: 'destructive' }); return;
    }
    const amount = currentOldGoldStone.amount ? parseFloat(currentOldGoldStone.amount) : w * r;
    setOldGoldStones(prev => [...prev, { name: currentOldGoldStone.name, weight: w, rate: r, amount }]);
    setCurrentOldGoldStone({ name: '', weight: '', rate: '', amount: '' });
  };

  const handleCreateOldGold = async () => {
    if (!oldGoldForm.customerPhone || !oldGoldForm.customerName || !oldGoldForm.grossWeight || !oldGoldForm.approxPurity || !oldGoldForm.goldRate) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' }); return;
    }
    try {
      await api.post('/exchange-estimates', {
        customer_phone: oldGoldForm.customerPhone,
        customer_name: oldGoldForm.customerName,
        gross_weight: parseFloat(oldGoldForm.grossWeight),
        less_dust: parseFloat(oldGoldForm.lessDust) || 0,
        less_stones: parseFloat(oldGoldForm.lessStones) || 0,
        approx_purity: parseFloat(oldGoldForm.approxPurity),
        refining_percentage: parseFloat(oldGoldForm.refiningPercentage) || 0,
        gold_rate: parseFloat(oldGoldForm.goldRate),
        cash_required: oldGoldForm.cashRequired,
        cash_deduction_percentage: parseFloat(oldGoldForm.cashDeductionPercentage) || 0,
        stones: oldGoldStones,
        stones_total: oldGoldCalc.stonesTotal,
        session_id: sessionId || null,
        lead_id: oldGoldLeadId || null,
      });
      toast({ title: 'Success', description: 'Old Gold exchange estimate created!' });
      setIsOldGoldOpen(false);
      // Refresh exchange estimates for this session
      if (sessionId) {
        const res = await api.get(`/exchange-estimates?session_id=${sessionId}`);
        setSessionExchangeEstimates(res.data);
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to create exchange estimate', variant: 'destructive' });
    }
  };

  const fmtCurrency = (amount) => `₹${Math.round(parseFloat(amount) || 0).toLocaleString('en-IN')}`;

  const openStatusDialog = (lead) => {
    setSelectedLead(lead);
    setStatusForm({ status: '', reason: '', reason_comment: '', follow_up_date: '', follow_up_salesman_id: '', park_action: '' });
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status || !statusForm.reason) {
      toast({ title: 'Status and reason are required', variant: 'destructive' });
      return;
    }
    if (statusForm.status === 'parked' && (!statusForm.follow_up_date || !statusForm.follow_up_salesman_id)) {
      toast({ title: 'Follow-up date and salesman are required for Parked', variant: 'destructive' });
      return;
    }
    if (statusForm.status === 'parked' && !statusForm.park_action) {
      toast({ title: 'Please pick what needs to happen next (park action)', variant: 'destructive' });
      return;
    }
    if (statusForm.reason === 'Other' && !statusForm.reason_comment) {
      toast({ title: 'Comment required for "Other" reason', variant: 'destructive' });
      return;
    }
    try {
      await api.patch(`/crm/leads/${selectedLead._id}/status`, statusForm);
      setStatusDialogOpen(false);
      fetchAll();
      toast({ title: `Lead marked as ${statusForm.status.toUpperCase()}` });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const handleReopenLead = async (leadId) => {
    try {
      await api.patch(`/crm/leads/${leadId}/reopen`);
      fetchAll();
      toast({ title: 'Lead reopened' });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const handleRescheduleFollowUp = async () => {
    if (!rescheduleDate || !rescheduleTarget) return;
    setRescheduling(true);
    try {
      await api.patch(`/crm/leads/${rescheduleTarget._id}/status`, {
        status: 'parked',
        reason: rescheduleTarget.reason,
        reason_comment: rescheduleTarget.reason_comment || '',
        follow_up_date: rescheduleDate,
        follow_up_salesman_id: rescheduleTarget.follow_up_salesman_id,
        park_action: rescheduleTarget.park_action,
      });
      const formatted = new Date(rescheduleDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      toast({ title: `Follow-up rescheduled to ${formatted}` });
      setRescheduleTarget(null);
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setRescheduling(false);
    }
  };

  const handleReopenSession = async () => {
    try {
      await api.patch(`/crm/sessions/${sessionId}/reopen`);
      fetchAll();
      toast({ title: 'Session reopened' });
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const getReasonGroups = () => {
    if (statusForm.status === 'lost') return lossReasons;
    if (statusForm.status === 'parked') return parkedReasons;
    if (statusForm.status === 'won') return wonReasons;
    return [];
  };

  const fmt = (n) => n ? `₹${Math.round(n).toLocaleString('en-IN')}` : '₹0';

  const handleLogVisit = async () => {
    if (!visitForm.date) {
      toast({ title: 'Please select a visit date', variant: 'destructive' });
      return;
    }
    setSubmittingVisit(true);
    try {
      await api.post(`/crm/sessions/${sessionId}/visits`, visitForm);
      toast({ title: `Visit #${(session?.visits?.length || 0) + 2} logged` });
      setVisitDialogOpen(false);
      setVisitForm({ date: '', summary: '' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setSubmittingVisit(false);
    }
  };

  // Merge messages + visit dividers into a single chronological timeline
  const chatItems = React.useMemo(() => {
    const items = messages.map(m => ({
      type: 'message',
      data: m,
      time: new Date(m.timestamp + 'Z'),
    }));
    (session?.visits || []).forEach(v => {
      items.push({
        type: 'visit',
        data: v,
        time: new Date(v.recorded_at),
      });
    });
    return items.sort((a, b) => a.time - b.time);
  }, [messages, session?.visits]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center"><RefreshCw className="w-8 h-8 animate-spin text-emerald-600" /></div>
  );

  if (!session) return (
    <div className="h-screen flex items-center justify-center text-gray-400">Session not found</div>
  );

  // Unread @mention messages (sent by others, after last read time)
  const unreadMentions = (() => {
    if (!currentUser?.username) return [];
    const lastReadAt = readState?.last_read_at ? new Date(readState.last_read_at) : null;
    const mentionRe = new RegExp(`@${currentUser.username}`, 'i');
    return messages.filter(m => {
      if (!m._id || m.sender_id === currentUser.id) return false;
      if (lastReadAt) {
        try {
          const ts = new Date(m.timestamp.endsWith('Z') ? m.timestamp : m.timestamp + 'Z');
          if (ts <= lastReadAt) return false;
        } catch { return false; }
      }
      return mentionRe.test(m.content || '');
    });
  })();

  const jumpToNextMention = () => {
    if (unreadMentions.length === 0) return;
    const msg = unreadMentions[mentionJumpIndex % unreadMentions.length];
    const el = document.getElementById(`msg-${msg._id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setMentionJumpIndex(i => i + 1);
  };

  // ── Session context bag — all state + handlers exposed to child tabs.
  //    Recreated each render (behaviorally identical to inline JSX before
  //    extraction). Cannot be memoized here — early returns above would
  //    violate rules-of-hooks. Splitting the context by concern (chat vs.
  //    leads vs. header) would enable per-slice memoization if perf demands.
  const sessionCtx = {
    session, setSession, leads, setLeads, messages, setMessages,
    isAdvisor,
    editingClientInfo, setEditingClientInfo,
    clientInfoForm, setClientInfoForm,
    savingClientInfo, handleSaveClientInfo,
    canReopenSession, handleReopenSession,
    canLogVisit, setVisitForm, setVisitDialogOpen,
    canManageLeads,
    showLeads, setShowLeads,
    setAddLeadOpen,
    linkedEstimateDetails,
    leadRepairEstimates,
    handleConfirmRepairEstimate,
    openEditLead, setDeleteLeadTarget,
    fmt, fmtCurrency,
    lossReasons, parkedReasons, wonReasons,
    openEstimateDetail, handleConfirmEstimate,
    sessionExchangeEstimates, setViewExchangeEst,
    openNotebook, handleCreateEstimateForLead, openOldGoldDialog,
    openStatusDialog, handleReopenLead,
    setRescheduleTarget, setRescheduleDate,
    handleConvertToOrder,
    chatItems,
    chatContainerRef, messagesEndRef, inputRef, imageInputRef, cameraInputRef, pressTimerRef,
    activeContextMsg, setActiveContextMsg,
    replyingTo, setReplyingTo,
    reactionTooltip, setReactionTooltip, handleReact,
    unreadMentions, jumpToNextMention,
    showTagMenu, setShowTagMenu, tagFilter, setTagFilter,
    taggablePeople,
    newMessage, setNewMessage,
    handleImageUpload, uploadingImage,
    showAttachMenu, setShowAttachMenu,
    showEmojiPicker, setShowEmojiPicker,
    handleSendMessage,
  };

  return (
    <SessionContext.Provider value={sessionCtx}>
    <div className="h-screen flex flex-col bg-gray-50" data-testid="session-detail-page">
      {/* Dark backdrop for long-press context menu */}
      {activeContextMsg && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setActiveContextMsg(null)}
          data-testid="context-menu-backdrop"
        />
      )}
      {/* Header — sticky green bar (extracted to SessionHeader.jsx) */}
      <SessionHeader />

      {/* Client Memory Card — surfaces prior visit history for the salesman */}
      {session && (
        <ClientMemoryCard
          clientPhone={session.client_phone}
          currentSessionId={sessionId}
        />
      )}

      {/* ── Tab Bar (Chat / Leads / Media) — sticky under the header ── */}
      <div className="flex-shrink-0 bg-white border-b flex" data-testid="session-tabs">
        {[
          { key: 'chat',  label: 'Chat',  count: messages.filter(m => m.type !== 'system' && m.type !== 'system_note').length },
          { key: 'leads', label: 'Leads', count: leads.length },
          { key: 'media', label: 'Media', count: messages.filter(m => m.type === 'image').length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors relative ${
              activeTab === t.key
                ? 'text-[#075E54]'
                : 'text-gray-400 hover:text-gray-600'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-black min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center ${
                  activeTab === t.key ? 'bg-[#075E54] text-white' : 'bg-gray-200 text-gray-600'
                }`}>{t.count}</span>
              )}
            </span>
            {activeTab === t.key && (
              <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-[#075E54] rounded-t" />
            )}
          </button>
        ))}
      </div>

      {/* Leads Panel - Collapsible (extracted to LeadsTab.jsx) */}
      {activeTab === 'leads' && <LeadsTab />}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <MediaTab messages={messages} sessionNumber={session.session_number} />
      )}

      {/* Chat area — extracted to ChatTab.jsx (messages + reopen banner + input + advisor notice) */}
      {activeTab === 'chat' && <ChatTab />}

      {/* Add Lead Dialog */}
      <Dialog open={addLeadOpen} onOpenChange={setAddLeadOpen}>
        <DialogContent data-testid="add-lead-dialog">
          <DialogHeader><DialogTitle>Add Product Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Main Group *</Label>
              <Select value={leadForm.main_group} onValueChange={v => setLeadForm(f => ({...f, main_group: v}))}>
                <SelectTrigger data-testid="group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {catalogGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Product Name *</Label>
              <Select value={leadForm.product_name} onValueChange={v => setLeadForm(f => ({...f, product_name: v}))}>
                <SelectTrigger data-testid="product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {catalogProducts.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value="__custom">Other (type below)</SelectItem>
                </SelectContent>
              </Select>
              {leadForm.product_name === '__custom' && (
                <Input
                  placeholder="Custom product name"
                  value={leadForm.custom_product_name}
                  onChange={e => setLeadForm(f => ({...f, custom_product_name: e.target.value}))}
                  className="mt-1"
                  autoFocus
                  data-testid="custom-product-input"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Estimated Value</Label>
                <Input type="number" value={leadForm.estimated_value} onChange={e => setLeadForm(f => ({...f, estimated_value: e.target.value}))} placeholder="₹ Estimated value" data-testid="est-value-input" />
              </div>
              <div className="space-y-1">
                <Label>Occasion Date</Label>
                <Input type="date" value={leadForm.occasion_date} onChange={e => setLeadForm(f => ({...f, occasion_date: e.target.value}))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Next Follow-up Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={leadForm.follow_up_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setLeadForm(f => ({...f, follow_up_date: e.target.value}))}
                data-testid="lead-followup-date-input"
              />
              <p className="text-[11px] text-gray-500">When will you next contact this customer? Required to prevent leads getting forgotten.</p>
            </div>
            {leadForm.main_group === 'Repair' && (
              <div className="space-y-1">
                <Label>Item Weight (g)</Label>
                <Input
                  type="number" inputMode="decimal" step="0.001"
                  value={leadForm.repair_weight}
                  onChange={e => setLeadForm(f => ({...f, repair_weight: e.target.value}))}
                  placeholder="Gross weight of the piece brought in"
                  data-testid="repair-weight-input"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label>{leadForm.main_group === 'Repair' ? 'Repair Details / Item Description' : 'Description'}</Label>
              <textarea value={leadForm.description} onChange={e => setLeadForm(f => ({...f, description: e.target.value}))} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder={leadForm.main_group === 'Repair' ? 'What needs repairing? Condition of the item, stones, etc. (Add photos of the old piece in the chat.)' : 'Requirements, design details...'} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLeadOpen(false)}>Cancel</Button>
            <Button onClick={handleAddLead} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-lead-btn">Add Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Estimate ledger — opened from a repair lead's "Add Estimate" */}
      <RepairEstimateDialog
        open={repairEstOpen}
        onOpenChange={setRepairEstOpen}
        leadId={repairEstLead?._id}
        sessionId={sessionId}
        customerName={session?.client_name}
        customerPhone={session?.client_phone}
        onSaved={() => { fetchAll(); }}
      />

      {/* Repair photo nudge — shown right after a repair lead is created */}
      <Dialog open={repairPhotoPromptOpen} onOpenChange={setRepairPhotoPromptOpen}>
        <DialogContent className="max-w-sm" data-testid="repair-photo-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Camera className="w-5 h-5 text-[#075E54]" /> Add photos of the piece
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 -mt-1">
            Capture the item the customer brought in. Photos go into this session's chat so the
            <strong> production team can see exactly what came in</strong> before they start the repair.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button
              onClick={() => { setRepairPhotoPromptOpen(false); setActiveTab('chat'); setPendingPhotoCapture('camera'); }}
              className="bg-[#075E54] hover:bg-[#064E46]"
              data-testid="repair-photo-take-btn"
            >
              <Camera className="w-4 h-4 mr-1.5" /> Take Photo
            </Button>
            <Button
              variant="outline"
              onClick={() => { setRepairPhotoPromptOpen(false); setActiveTab('chat'); setPendingPhotoCapture('gallery'); }}
              data-testid="repair-photo-choose-btn"
            >
              <ImageIcon className="w-4 h-4 mr-1.5" /> Choose Photo
            </Button>
          </div>
          <button
            onClick={() => setRepairPhotoPromptOpen(false)}
            className="text-xs text-gray-600 hover:text-gray-900 mt-1 mx-auto py-2"
            data-testid="repair-photo-skip-btn"
          >
            Skip for now — I'll add them later in the chat
          </button>
        </DialogContent>
      </Dialog>

      {/* Update Lead Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle>Update Lead Status — {selectedLead?.product_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Status *</Label>
              <div className="grid grid-cols-3 gap-2">
                {['won', 'lost', 'parked'].map(s => (
                  <button key={s} onClick={() => setStatusForm(f => ({
                    ...f,
                    status: s,
                    reason: '',
                    reason_comment: '',
                    follow_up_date: '',
                    park_action: '',
                    // Lock follow-up salesman to session's assigned salesman
                    follow_up_salesman_id: s === 'parked' ? (session?.assigned_salesman_id || '') : '',
                  }))}
                    className={`py-2.5 rounded-lg text-sm font-semibold border-2 transition-all ${statusForm.status === s
                      ? s === 'won' ? 'bg-green-500 text-white border-green-500' : s === 'lost' ? 'bg-red-500 text-white border-red-500' : 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white border-gray-300 text-gray-600'}`}
                    data-testid={`status-btn-${s}`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {statusForm.status && (
              <div className="space-y-1">
                <Label>Reason *</Label>
                <Select value={statusForm.reason} onValueChange={v => setStatusForm(f => ({...f, reason: v}))}>
                  <SelectTrigger data-testid="reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    {getReasonGroups().map(group => (
                      <SelectGroup key={group.category}>
                        <SelectLabel className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">{group.category}</SelectLabel>
                        {(group.reasons || []).map(r => (
                          <SelectItem key={r.id} value={r.reason}>{r.reason}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {statusForm.reason === 'Other' && (
              <div className="space-y-1">
                <Label>Comment *</Label>
                <Input value={statusForm.reason_comment} onChange={e => setStatusForm(f => ({...f, reason_comment: e.target.value}))} placeholder="Please specify..." data-testid="reason-comment" />
              </div>
            )}
            {statusForm.status === 'parked' && (
              <>
                <div className="space-y-1">
                  <Label>What needs to happen next? *</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="park-action-picker">
                    {parkActions.map(pa => (
                      <button
                        key={pa.key}
                        type="button"
                        onClick={() => setStatusForm(f => ({ ...f, park_action: pa.key }))}
                        className={`py-2 px-2 rounded-lg text-xs font-semibold border-2 transition-all text-left ${
                          statusForm.park_action === pa.key
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-amber-300'
                        }`}
                        data-testid={`park-action-${pa.key}`}
                      >
                        <div className="font-semibold">{pa.label}</div>
                        <div className={`text-[10px] mt-0.5 ${statusForm.park_action === pa.key ? 'text-amber-100' : 'text-gray-400'}`}>
                          Nudge in {pa.nudge_days}d
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Follow-up Date *</Label>
                  <Input type="date" value={statusForm.follow_up_date} onChange={e => setStatusForm(f => ({...f, follow_up_date: e.target.value}))} data-testid="followup-date" />
                </div>
                <div className="space-y-1">
                  <Label>Follow-up Salesman</Label>
                  <div className="px-3 py-2 border rounded-md bg-gray-50 text-sm text-gray-700 flex items-center gap-2" data-testid="followup-salesman-display">
                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span>{session?.assigned_salesman_name || 'Assigned salesman'}</span>
                    <span className="ml-auto text-xs text-gray-400">Auto-assigned</span>
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Won gate warning */}
          {statusForm.status === 'won' && (() => {
            const isRepair = (selectedLead?.main_group || '') === 'Repair';
            const hasConfirmed = isRepair
              ? (leadRepairEstimates[selectedLead?._id] || []).some(e => e.status === 'active')
              : (linkedEstimateDetails[selectedLead?._id] || []).some(e => e.status === 'confirmed');
            if (!hasConfirmed) return (
              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span>A <strong>confirmed {isRepair ? 'repair ' : ''}estimate</strong> is required to mark as Won. Tap <strong>Confirm</strong> on one of the {isRepair ? 'repair estimates' : 'linked estimates'} first.</span>
              </div>
            );
            return null;
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleStatusUpdate}
              className="bg-[#075E54] hover:bg-[#064E46] disabled:opacity-50"
              data-testid="confirm-status-btn"
              disabled={statusForm.status === 'won' && !(
                (selectedLead?.main_group || '') === 'Repair'
                  ? (leadRepairEstimates[selectedLead?._id] || []).some(e => e.status === 'active')
                  : (linkedEstimateDetails[selectedLead?._id] || []).some(e => e.status === 'confirmed')
              )}
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Estimate Dialog */}
      <Dialog open={linkEstimateOpen} onOpenChange={setLinkEstimateOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="link-estimate-dialog">
          <DialogHeader>
            <DialogTitle>Link Estimate — {linkLeadTarget?.product_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Select an estimate for {session?.client_name} ({session?.client_phone})</p>
            {(() => {
              const alreadyLinked = linkLeadTarget?.linked_estimate_ids || (linkLeadTarget?.linked_estimate_id ? [linkLeadTarget.linked_estimate_id] : []);
              const available = customerEstimates.filter(est => !alreadyLinked.includes(est.id));
              if (available.length === 0 && customerEstimates.length === 0) {
                return <p className="text-sm text-gray-400 text-center py-4">No estimates found for this customer</p>;
              }
              if (available.length === 0) {
                return <p className="text-sm text-gray-400 text-center py-4">All estimates already linked to this lead</p>;
              }
              return available.map(est => (
                <div
                  key={est.id || est.estimate_number}
                  onClick={() => handleLinkEstimate(est.id)}
                  className="border rounded-lg p-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  data-testid={`link-est-${est.estimate_number}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm">{est.estimate_number}</p>
                      <p className="text-xs text-gray-500">
                        {est.main_group && est.product ? `${est.main_group} - ${est.product}` : est.main_group || est.product || ''}
                        {' | '}{new Date(est.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">{fmt(est.total_amount)}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${est.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {est.status}
                      </span>
                    </div>
                  </div>
                </div>
              ));
            })()}
            {canManageLeads && session?.client_name && session?.client_phone && (
              <Button variant="outline" className="w-full" onClick={() => handleCreateEstimateForLead(linkLeadTarget)} data-testid="create-estimate-for-lead-btn">
                <Plus className="w-4 h-4 mr-2" /> Create New Estimate
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Estimate Dialog */}
      <EstimateFormDialog
        open={createEstimateOpen}
        onOpenChange={setCreateEstimateOpen}
        onSaved={handleEstimateSaved}
        prefill={estimatePrefill}
        lockCustomer={!!session?.client_phone}
        duplicateData={duplicateEstimateData}
      />

      {/* Today's Gold Rate not-updated gate */}
      <GoldRateGateDialog
        {...goldGateDialogProps}
        userRole={currentUser?.role}
        onUpdateNow={() => navigate('/gold-prices')}
      />

      {/* Estimate Detail Popup */}
      <EstimateDetailPopup
        open={viewEstimateOpen}
        onOpenChange={setViewEstimateOpen}
        estimate={viewEstimateData}
        onDuplicate={handleDuplicateEstimate}
        onDeleted={handleEstimateDeleted}
        onConfirmed={handleEstimateConfirmed}
      />

      {/* Old Gold Estimate Detail Dialog */}
      <Dialog open={!!viewExchangeEst} onOpenChange={o => !o && setViewExchangeEst(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {viewExchangeEst?.estimate_number} — Old Gold Exchange
            </DialogTitle>
          </DialogHeader>
          {viewExchangeEst && (
            <div className="space-y-4 text-sm">
              {/* Customer */}
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div><span className="font-medium">Customer</span><p className="text-gray-900">{viewExchangeEst.customer_name || '—'}</p></div>
                <div><span className="font-medium">Phone</span><p className="text-gray-900">{viewExchangeEst.customer_phone}</p></div>
              </div>
              {/* Weight */}
              <div className="border rounded-lg p-3 space-y-1 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Weight Details</p>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-gray-500">Gross Weight</span><span className="font-medium text-right">{viewExchangeEst.gross_weight} g</span>
                  {viewExchangeEst.less_dust > 0 && <><span className="text-gray-500">Less Dust</span><span className="font-medium text-right">−{viewExchangeEst.less_dust} g</span></>}
                  {viewExchangeEst.less_stones > 0 && <><span className="text-gray-500">Less Stones</span><span className="font-medium text-right">−{viewExchangeEst.less_stones} g</span></>}
                  <span className="text-gray-500">Net Weight</span><span className="font-medium text-right">{viewExchangeEst.net_weight} g</span>
                </div>
              </div>
              {/* Purity */}
              <div className="border rounded-lg p-3 space-y-1 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Purity & Refining</p>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-gray-500">Approx Purity</span><span className="font-medium text-right">{viewExchangeEst.approx_purity}%</span>
                  <span className="text-gray-500">Fine Weight</span><span className="font-medium text-right">{viewExchangeEst.fine_weight} g</span>
                  {viewExchangeEst.refining_percentage > 0 && <><span className="text-gray-500">Refining Loss</span><span className="font-medium text-right">{viewExchangeEst.refining_percentage}%</span></>}
                  <span className="text-gray-500">Final Gold Wt</span><span className="font-medium text-right">{viewExchangeEst.final_gold_weight} g</span>
                </div>
              </div>
              {/* Stones */}
              {viewExchangeEst.stones?.length > 0 && (
                <div className="border rounded-lg p-3 bg-amber-50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Stones in Gold</p>
                  {viewExchangeEst.stones.map((s, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span className="text-gray-700">{s.name || 'Stone'} · {s.weight}ct @ ₹{Number(s.rate).toLocaleString('en-IN')}/ct</span>
                      <span className="font-medium">₹{Math.round(s.amount).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs font-semibold text-amber-700 border-t border-amber-200 mt-1 pt-1">
                    <span>Stones Total</span><span>{fmtCurrency(viewExchangeEst.stones_total)}</span>
                  </div>
                </div>
              )}
              {/* Value */}
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 text-center">
                {viewExchangeEst.stones_total > 0 && (
                  <div className="grid grid-cols-2 text-xs text-gray-600 mb-2 gap-y-0.5">
                    <span className="text-left">Gold Value</span><span className="text-right">{fmtCurrency(viewExchangeEst.exchange_value - viewExchangeEst.stones_total)}</span>
                    <span className="text-left">Stones Value</span><span className="text-right">{fmtCurrency(viewExchangeEst.stones_total)}</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">Final Exchange Value</p>
                <p className="text-2xl font-bold text-amber-700">{fmtCurrency(viewExchangeEst.exchange_value)}</p>
                <p className="text-xs text-gray-500">{viewExchangeEst.final_gold_weight}g × ₹{Number(viewExchangeEst.gold_rate).toLocaleString('en-IN')}/g</p>
              </div>
              {/* Cash */}
              {viewExchangeEst.cash_required && (
                <div className="border rounded-lg p-3 bg-blue-50 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Cash Deduction</span><span>{viewExchangeEst.cash_deduction_percentage}%</span></div>
                  <div className="flex justify-between font-semibold text-blue-700 mt-1"><span>Cash to Customer</span><span>{fmtCurrency(viewExchangeEst.final_cash_amount)}</span></div>
                </div>
              )}
              <p className="text-[10px] text-gray-400 text-right">
                By {viewExchangeEst.created_by_name} · {viewExchangeEst.created_at ? new Date(viewExchangeEst.created_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lead Notebook Dialog */}
      <Dialog open={notebookOpen} onOpenChange={(open) => {
        if (!open && notebookLead) {
          // Save draft when closing
          if (newNote.trim() || noteImages.length > 0) {
            setNoteDrafts(prev => ({ ...prev, [notebookLead._id]: { text: newNote, images: noteImages } }));
          }
        }
        setNotebookOpen(open);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0" data-testid="notebook-dialog">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-purple-50">
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span>#{notebookLead?.lead_number} {notebookLead?.product_name}</span>
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-0.5">{notebookLead?.main_group} — {leadNotes.length} note{leadNotes.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Notes Feed */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
            {leadNotes.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{isAdvisor ? 'No notes yet.' : 'No notes yet. Add your first note below.'}</p>
              </div>
            ) : leadNotes.map(note => (
              <div key={note._id} className="bg-white border rounded-lg p-3 group" data-testid={`note-${note._id}`}>
                {note.content && <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.content}</p>}
                {note.images?.length > 0 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto">
                    {note.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-24 h-24 object-cover rounded-lg border cursor-pointer flex-shrink-0" onClick={() => window.open(img, '_blank')} onError={(e) => { e.target.style.display='none'; }} />
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-gray-400">
                    {note.created_by_name} — {(() => { try { return formatDistanceToNow(new Date(note.created_at + 'Z'), { addSuffix: true }); } catch { return ''; } })()}
                  </p>
                  {(note.created_by === currentUser?.id || currentUser?.role === 'admin') && (
                    <button onClick={() => handleDeleteNote(note._id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Note Input */}
          {!isAdvisor && (
          <div className="border-t p-3 space-y-2">
            {noteImages.length > 0 && (
              <div className="flex gap-2 overflow-x-auto">
                {noteImages.map((img, i) => (
                  <div key={i} className="relative flex-shrink-0">
                    <img src={img} alt="" className="w-16 h-16 object-cover rounded-lg border" onError={(e) => { e.target.style.display='none'; }} />
                    <button onClick={() => setNoteImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input type="file" ref={noteImageRef} accept="image/*" onChange={handleNoteImageUpload} className="hidden" />
              <Button size="icon" variant="ghost" onClick={() => noteImageRef.current?.click()} disabled={uploadingNote} className="text-gray-500 hover:text-purple-600 flex-shrink-0">
                {uploadingNote ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              </Button>
              <textarea
                ref={noteTextareaRef}
                value={newNote}
                onChange={e => {
                  setNewNote(e.target.value);
                  // Auto-expand
                  const el = noteTextareaRef.current;
                  if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 160) + 'px'; }
                }}
                placeholder="Add a note... (Enter for new line)"
                className="flex-1 px-3 py-2 border rounded-md text-sm resize-none overflow-hidden"
                style={{ minHeight: '40px' }}
                data-testid="note-input"
              />
              <Button size="icon" onClick={handleAddNote} className="bg-purple-600 hover:bg-purple-700" data-testid="add-note-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Old Gold Exchange Dialog */}
      <Dialog open={isOldGoldOpen} onOpenChange={setIsOldGoldOpen}>
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-700">Old Gold Exchange Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm">Customer Phone *</Label>
                <PhoneInput
                  value={normalizePhone(oldGoldForm.customerPhone)}
                  onChange={val => setOldGoldForm(prev => ({ ...prev, customerPhone: val }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Customer Name *</Label>
                <Input value={oldGoldForm.customerName} onChange={e => setOldGoldForm({ ...oldGoldForm, customerName: e.target.value })} className="py-5" />
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Weight Details</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Gross Weight (g) *</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={oldGoldForm.grossWeight} onChange={e => setOldGoldForm({ ...oldGoldForm, grossWeight: e.target.value })} className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Less Dust (g)</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={oldGoldForm.lessDust} onChange={e => setOldGoldForm({ ...oldGoldForm, lessDust: e.target.value })} className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Less Stones (g)</Label>
                  <Input type="number" inputMode="decimal" step="0.001" value={oldGoldForm.lessStones} onChange={e => setOldGoldForm({ ...oldGoldForm, lessStones: e.target.value })} className="py-5" />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Net Weight</span>
                  <span className="font-bold text-emerald-700">{oldGoldCalc.netWeight.toFixed(3)}g</span>
                </div>
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Purity &amp; Refining</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Approx Purity (%) *</Label>
                  <Input type="number" inputMode="decimal" step="0.1" value={oldGoldForm.approxPurity} onChange={e => setOldGoldForm({ ...oldGoldForm, approxPurity: e.target.value })} placeholder="e.g. 85" className="py-5" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Refining Loss (%)</Label>
                  <Input type="number" inputMode="decimal" step="0.1" value={oldGoldForm.refiningPercentage} onChange={e => setOldGoldForm({ ...oldGoldForm, refiningPercentage: e.target.value })} placeholder="e.g. 5" className="py-5" />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fine Weight (Net × Purity)</span>
                  <span className="font-bold text-emerald-700">{oldGoldCalc.fineWeight.toFixed(3)}g</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Final Gold Weight (after refining)</span>
                  <span className="font-bold text-emerald-700">{oldGoldCalc.finalGoldWeight.toFixed(3)}g</span>
                </div>
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Gold Rate &amp; Value</h3>
              <div className="space-y-1">
                <Label className="text-sm">Gold Rate (₹/gram) *</Label>
                <Input type="number" inputMode="numeric" value={oldGoldForm.goldRate} onChange={e => setOldGoldForm({ ...oldGoldForm, goldRate: e.target.value })} className="py-5" />
              </div>
            </div>

            {/* Stones in Gold */}
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Stones in Gold <span className="text-xs font-normal text-gray-400">(optional)</span></h3>
              {/* Stone entry row */}
              <div className="grid grid-cols-12 gap-1.5 items-end">
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Name</Label>
                  <Input
                    placeholder="Diamond"
                    value={currentOldGoldStone.name}
                    onChange={e => setCurrentOldGoldStone(s => ({ ...s, name: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid="og-stone-name"
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Wt (ct)</Label>
                  <Input
                    type="number" inputMode="decimal" step="0.01" placeholder="0.00"
                    value={currentOldGoldStone.weight}
                    onChange={e => {
                      const w = e.target.value;
                      const auto = w && currentOldGoldStone.rate ? String(parseFloat(w) * parseFloat(currentOldGoldStone.rate)) : '';
                      setCurrentOldGoldStone(s => ({ ...s, weight: w, amount: auto }));
                    }}
                    className="h-9 text-sm"
                    data-testid="og-stone-weight"
                  />
                </div>
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs text-gray-500">Rate ₹</Label>
                  <Input
                    type="number" inputMode="numeric" placeholder="0"
                    value={currentOldGoldStone.rate}
                    onChange={e => {
                      const r = e.target.value;
                      const auto = r && currentOldGoldStone.weight ? String(parseFloat(currentOldGoldStone.weight) * parseFloat(r)) : '';
                      setCurrentOldGoldStone(s => ({ ...s, rate: r, amount: auto }));
                    }}
                    className="h-9 text-sm"
                    data-testid="og-stone-rate"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs text-gray-500">Amt ₹</Label>
                  <Input
                    type="number" inputMode="numeric" placeholder="auto"
                    value={currentOldGoldStone.amount}
                    onChange={e => setCurrentOldGoldStone(s => ({ ...s, amount: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid="og-stone-amount"
                  />
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={handleAddOldGoldStone}
                    className="h-9 w-full flex items-center justify-center rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                    data-testid="og-add-stone-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Stone list */}
              {oldGoldStones.length > 0 && (
                <div className="space-y-1.5">
                  {oldGoldStones.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800">{s.name || 'Stone'}</span>
                        <span className="text-gray-500 ml-2">{s.weight}ct @ ₹{s.rate.toLocaleString('en-IN')}/ct</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-semibold text-amber-700">₹{Math.round(s.amount).toLocaleString('en-IN')}</span>
                        <button
                          type="button"
                          onClick={() => setOldGoldStones(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                          data-testid={`og-remove-stone-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold text-amber-700 px-3">
                    <span>Stones Total</span>
                    <span>{fmtCurrency(oldGoldCalc.stonesTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Final Exchange Value */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              {oldGoldCalc.stonesTotal > 0 ? (
                <div className="space-y-1 mb-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Gold Value</span>
                    <span>{fmtCurrency(oldGoldCalc.goldValue)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Stones Value</span>
                    <span>{fmtCurrency(oldGoldCalc.stonesTotal)}</span>
                  </div>
                  <div className="border-t border-amber-200 pt-1" />
                </div>
              ) : null}
              <p className="text-sm text-gray-600 text-center">Final Exchange Value</p>
              <p className="text-2xl font-bold text-amber-700 text-center">{fmtCurrency(oldGoldCalc.exchangeValue)}</p>
              <p className="text-xs text-gray-500 text-center">
                {oldGoldCalc.finalGoldWeight.toFixed(3)}g × ₹{(parseFloat(oldGoldForm.goldRate) || 0).toLocaleString('en-IN')}/g
                {oldGoldCalc.stonesTotal > 0 && ` + ${fmtCurrency(oldGoldCalc.stonesTotal)} stones`}
              </p>
            </div>
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center space-x-3">
                <input type="checkbox" id="oldGoldCash" checked={oldGoldForm.cashRequired} onChange={e => setOldGoldForm({ ...oldGoldForm, cashRequired: e.target.checked })} className="w-4 h-4 text-emerald-600" />
                <Label htmlFor="oldGoldCash" className="cursor-pointer text-sm font-semibold">Cash Required?</Label>
              </div>
              {oldGoldForm.cashRequired && (
                <>
                  <div className="space-y-1">
                    <Label className="text-sm">Cash Deduction (%)</Label>
                    <Input type="number" inputMode="decimal" step="0.1" value={oldGoldForm.cashDeductionPercentage} onChange={e => setOldGoldForm({ ...oldGoldForm, cashDeductionPercentage: e.target.value })} placeholder="e.g. 3" className="py-5" />
                  </div>
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600">Final Cash Amount</p>
                    <p className="text-2xl font-bold text-green-700">{fmtCurrency(oldGoldCalc.finalCash)}</p>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsOldGoldOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateOldGold} className="bg-amber-500 hover:bg-amber-600">Save Exchange Estimate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Visit Dialog */}
      <Dialog open={visitDialogOpen} onOpenChange={setVisitDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" />
              Log New Visit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Visit Date *</Label>
              <input
                type="date"
                value={visitForm.date}
                onChange={e => setVisitForm(f => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md text-sm"
                data-testid="visit-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Summary <span className="text-gray-400 text-xs">(optional)</span></Label>
              <textarea
                value={visitForm.summary}
                onChange={e => setVisitForm(f => ({ ...f, summary: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border rounded-md text-sm resize-none"
                placeholder="Key discussion points, outcome, next steps..."
                data-testid="visit-summary-input"
              />
            </div>
            <p className="text-xs text-gray-400">
              Visit #{(session?.visits?.length || 0) + 2} will be added to this session.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisitDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleLogVisit}
              disabled={submittingVisit}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-log-visit-btn"
            >
              {submittingVisit ? 'Saving...' : 'Log Visit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Lead Confirmation */}
      <AlertDialog open={!!deleteLeadTarget} onOpenChange={(open) => { if (!open) setDeleteLeadTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete lead &quot;#{deleteLeadTarget?.lead_number} {deleteLeadTarget?.product_name}&quot; and all its notes. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLead} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-lead-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Lead Dialog (admin only) */}
      <Dialog open={!!editLeadTarget} onOpenChange={(open) => { if (!open) setEditLeadTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead #{editLeadTarget?.lead_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Main Group</Label>
              <Select value={editLeadForm.main_group} onValueChange={v => setEditLeadForm(f => ({...f, main_group: v}))}>
                <SelectTrigger data-testid="edit-lead-main-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  {catalogGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Product Name *</Label>
              <Select value={editLeadForm.product_name} onValueChange={v => setEditLeadForm(f => ({...f, product_name: v}))}>
                <SelectTrigger data-testid="edit-lead-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {catalogProducts.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value="__custom">Other (custom)</SelectItem>
                </SelectContent>
              </Select>
              {editLeadForm.product_name === '__custom' && (
                <Input
                  placeholder="Custom product name"
                  value={editLeadForm.custom_product_name}
                  onChange={e => setEditLeadForm(f => ({...f, custom_product_name: e.target.value}))}
                  className="mt-1"
                  autoFocus
                  data-testid="edit-lead-custom-product"
                />
              )}
            </div>
            <div className="space-y-1">
              <Label>Estimated Value (₹)</Label>
              <Input type="number" value={editLeadForm.estimated_value} onChange={e => setEditLeadForm(f => ({...f, estimated_value: e.target.value}))} placeholder="₹ Estimated value" data-testid="edit-lead-est-value" />
            </div>
            <div className="space-y-1">
              <Label>Occasion Date</Label>
              <Input type="date" value={editLeadForm.occasion_date} onChange={e => setEditLeadForm(f => ({...f, occasion_date: e.target.value}))} data-testid="edit-lead-occasion" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <textarea value={editLeadForm.description} onChange={e => setEditLeadForm(f => ({...f, description: e.target.value}))} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Requirements, design details..." data-testid="edit-lead-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLeadTarget(null)}>Cancel</Button>
            <Button onClick={handleSaveEditLead} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-edit-lead-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link-to-lead prompt when customer info changed on duplicate */}
      <AlertDialog open={showLinkPrompt} onOpenChange={setShowLinkPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Link to original lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Customer details were changed. Do you want to keep this estimate linked to the original lead <strong>&quot;{viewEstimateLeadContext?.product_name}&quot;</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowLinkPrompt(false); setPendingLinkEstimate(null); }}>
              No, save standalone
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-link-estimate-btn"
              onClick={async () => {
                if (pendingLinkEstimate) {
                  try {
                    await api.patch(`/crm/leads/${pendingLinkEstimate.leadId}/link-estimate`, { estimate_id: pendingLinkEstimate.estimateId });
                  } catch (e) { console.error('Link failed:', e); }
                }
                setShowLinkPrompt(false);
                setPendingLinkEstimate(null);
                fetchAll();
              }}
            >
              Yes, link to lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule Follow-up Dialog */}
      <Dialog open={!!rescheduleTarget} onOpenChange={(open) => { if (!open) setRescheduleTarget(null); }}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Follow-up — {rescheduleTarget?.product_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {rescheduleTarget?.follow_up_date && (
              <p className="text-sm text-gray-500">
                Current date: <span className="font-medium text-amber-600">
                  {new Date(rescheduleTarget.follow_up_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </p>
            )}
            <div className="space-y-1">
              <Label>New Follow-up Date *</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={e => setRescheduleDate(e.target.value)}
                data-testid="reschedule-date-input"
                autoFocus
              />
            </div>
            {rescheduleTarget?.follow_up_salesman_name && (
              <p className="text-xs text-gray-400">Assigned to: {rescheduleTarget.follow_up_salesman_name}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleTarget(null)}>Cancel</Button>
            <Button
              onClick={handleRescheduleFollowUp}
              disabled={!rescheduleDate || rescheduling}
              className="bg-[#075E54] hover:bg-[#064E46] disabled:opacity-50"
              data-testid="reschedule-save-btn"
            >
              {rescheduling ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Who Reacted — WhatsApp-style bottom sheet ─────────────────── */}
      {reactionTooltip && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setReactionTooltip(null)}
        >
          {/* Scrim */}
          <div className="absolute inset-0 bg-black/30" />

          {/* Sheet */}
          <div
            className="relative bg-white w-full max-w-sm rounded-t-2xl shadow-2xl pb-safe overflow-hidden"
            onClick={e => e.stopPropagation()}
            style={{ animation: 'slideUp 0.22s ease-out' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-9 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center gap-3 px-5 pt-2 pb-3 border-b border-gray-100">
              <span className="text-3xl leading-none">{reactionTooltip.emoji}</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {reactionTooltip.users.length === 1 ? '1 reaction' : `${reactionTooltip.users.length} reactions`}
                </p>
                <p className="text-xs text-gray-400">Tap your name to remove</p>
              </div>
            </div>

            {/* Reactor list */}
            <div className="px-5 py-2 max-h-64 overflow-y-auto space-y-1">
              {reactionTooltip.users.map((u, i) => {
                const uid = _getRId(u);
                const uname = _getRName(u) || 'Unknown';
                const isCurrentUser = uid === currentUser?.id;
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between py-2.5 rounded-xl px-2 transition-colors ${isCurrentUser ? 'bg-emerald-50 cursor-pointer active:bg-emerald-100' : ''}`}
                    onClick={isCurrentUser ? () => { handleReact(reactionTooltip.msgId, reactionTooltip.emoji); setReactionTooltip(null); } : undefined}
                    data-testid={isCurrentUser ? 'reaction-remove-self' : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {uname[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{uname}</p>
                        {isCurrentUser && <p className="text-xs text-emerald-600">You — tap to remove</p>}
                      </div>
                    </div>
                    {isCurrentUser && (
                      <span className="text-lg text-gray-300">×</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom padding for home indicator */}
            <div className="h-4" />
          </div>
        </div>
      )}

      {/* Print Prompt — shown after a new estimate is saved */}
      <Dialog open={showPrintPrompt} onOpenChange={setShowPrintPrompt}>
        <DialogContent className="max-w-sm" data-testid="print-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Estimate Saved!</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Would you like to print the invoice now?</p>
          <div className="flex gap-3 mt-2">
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                setShowPrintPrompt(false);
                setViewEstimateData(printPromptEstimate);
                setViewEstimateOpen(true);
              }}
              data-testid="print-prompt-yes-btn"
            >
              Open &amp; Print
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowPrintPrompt(false)}
              data-testid="print-prompt-no-btn"
            >
              Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
    </SessionContext.Provider>
  );
};

export default SessionDetail;
